import { useCallback, useEffect, useMemo, useState } from 'react';
import resourceSharingService from '../services/resourceSharingService';
import { apiErrorText, describeApiError, mapApiError } from '../utils/apiError';
import { SHARED_KB_COPY } from '../constants/sharedKnowledgeCopy';

// Guide §7: friendlier text for the two refusals a user can act on. Anything
// else falls back to the backend's own `detail`.
const SAVE_ERROR_RULES = [
  { status: 400, match: /embedding model/i, message: SHARED_KB_COPY.embeddingMismatch },
  {
    status: 400,
    match: /is not one of your DIM3 folders|No active share grants you access/i,
    message: SHARED_KB_COPY.folderNotAllowed,
  },
];

const describeSaveError = (err) => {
  const raw = apiErrorText(err);
  const error = new Error(mapApiError(err, SAVE_ERROR_RULES, SHARED_KB_COPY.saveFailed));
  error.status = err?.status;
  // The embedding-model refusal is replaced by plainer copy; keep the backend's
  // own sentence so support can see which models clashed.
  error.detail = err?.status === 400 && /embedding model/i.test(raw) ? raw : '';
  return error;
};

/** API block → the shape KnowledgeBaseD3Dialog edits. */
const toSelection = (block) => {
  if (!block) return null;
  const entries = Array.isArray(block.knowledge_bases) ? block.knowledge_bases : [];
  return {
    ragType: block.rag_type || 'RAG',
    // The dialog keys and updates entries by `id`.
    knowledgeBases: entries.map((entry, index) => ({ ...entry, id: entry?.id ?? `my-dim3-${index}` })),
  };
};

// Shared folders carry the share's `resource_id`, which is a string — the
// endpoint takes folder ids as numbers.
const toFolderId = (id) => (/^\d+$/.test(String(id)) ? Number(id) : id);

// `id` is the dialog's own row key, not part of the entry.
const toApiBlock = (formData) => ({
  rag_type: formData?.ragType || 'RAG',
  knowledge_bases: (formData?.knowledgeBases || []).map(({ id, ...entry }) => ({
    ...entry,
    knowledgeBase: Array.isArray(entry.knowledgeBase) ? entry.knowledgeBase.map(toFolderId) : [],
  })),
});

const countFolders = (selection) =>
  (selection?.knowledgeBases || []).reduce(
    (total, entry) => total + (Array.isArray(entry?.knowledgeBase) ? entry.knowledgeBase.length : 0),
    0
  );

/**
 * A share recipient's own DIM3 folders for one agent (`/diy-agents/{id}/my-dim3`).
 *
 * The owner's DIM3 folders are never searched for a recipient: DIM3 is off for
 * them until they pick their own, and the pick applies to that user only. The
 * GET is also how ownership is told apart when only the id is known — it 400s
 * for the owner and 404s when the agent is not shared with the caller, and both
 * leave `isRecipient` false so callers render nothing.
 *
 * `pickerOpen` / `removeOpen` live here so every entry point (builder card, chat
 * bar, chat warning) drives the one dialog rendered by whoever created the hook.
 */
const useMyDim3 = (agentId, { enabled = true } = {}) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  // Another agent's state must never show against this one.
  useEffect(() => {
    setState(null);
    setPickerOpen(false);
    setRemoveOpen(false);
  }, [agentId, enabled]);

  useEffect(() => {
    if (!enabled || !agentId) {
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    resourceSharingService.getMyDim3(agentId)
      .then((result) => {
        if (!cancelled) setState(result && result.mode ? result : null);
      })
      // Fail quiet: the backend enforces the rules on every run regardless.
      .catch(() => {
        if (!cancelled) setState(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [agentId, enabled, reloadNonce]);

  const refresh = useCallback(() => setReloadNonce((n) => n + 1), []);
  const openPicker = useCallback(() => setPickerOpen(true), []);
  const closePicker = useCallback(() => setPickerOpen(false), []);
  const openRemove = useCallback(() => setRemoveOpen(true), []);
  const closeRemove = useCallback(() => setRemoveOpen(false), []);

  /** Replaces the previous selection. Throws an Error with `.message` (+ `.detail`). */
  const save = useCallback(async (formData) => {
    try {
      const result = await resourceSharingService.saveMyDim3(agentId, toApiBlock(formData));
      if (result && result.mode) setState(result);
      else refresh();
      return result;
    } catch (err) {
      throw describeSaveError(err);
    }
  }, [agentId, refresh]);

  /** Removes the selection — DIM3 is off for this user again. */
  const reset = useCallback(async () => {
    try {
      await resourceSharingService.resetMyDim3(agentId);
      setState((prev) => (prev ? { ...prev, selected: false, knowledge_base_dim3: null } : prev));
      refresh();
    } catch (err) {
      throw new Error(describeApiError(err, SHARED_KB_COPY.removeFailed));
    }
  }, [agentId, refresh]);

  const selection = useMemo(() => toSelection(state?.knowledge_base_dim3), [state]);

  return useMemo(() => ({
    loading,
    isRecipient: Boolean(state),
    // 'personal' = the user's own selection applies; 'owner' = shared-memory
    // share, which runs on the owner's folders and takes no selection.
    mode: state?.mode || null,
    canSelect: Boolean(state?.can_select),
    selected: Boolean(state?.selected),
    // Why canSelect is false: 'shared_memory' | 'agent_dim3_disabled'.
    reason: state?.reason || null,
    selection,
    folderCount: countFolders(selection),
    pickerOpen,
    removeOpen,
    openPicker,
    closePicker,
    openRemove,
    closeRemove,
    save,
    reset,
    refresh,
  }), [
    loading, state, selection, pickerOpen, removeOpen,
    openPicker, closePicker, openRemove, closeRemove, save, reset, refresh,
  ]);
};

export default useMyDim3;
